import { useState } from "react";
import type {
  ServiceCreateInput, Environment, DeploymentType, Instrumentation, ConnectionMode, AuthMethod, DiscoveryProvider,
} from "@coremonit/types";

export interface WizardData {
  // Step 1
  name: string; description: string; owner: string; team: string; tags: string[];
  environment: Environment; language: string; runtime: string; version: string;
  // Step 2
  deploymentType: DeploymentType;
  cluster?: string; namespace?: string; region?: string;
  // Step 3
  instrumentation: Instrumentation[];
  // Step 4
  connectionMode: ConnectionMode;
  connectionEndpoint?: string;
  discovery?: DiscoveryProvider;
  // Step 5
  authMethod: AuthMethod;
  authSecretRef?: string;
  authToken?: string;
  // Step 6+ (computed at run-time)
  validationResults?: Array<{ id: string; label: string; status: string; latencyMs: number; message: string }>;
  baseline?: { window: string; baseline: Record<string, number> };
  aiRecommendation?: string;
  createdServiceId?: string;
}

const DEFAULT: WizardData = {
  name: "", description: "", owner: "", team: "", tags: [],
  environment: "production",
  language: "TypeScript",
  runtime: "Node 20",
  version: "0.1.0",
  deploymentType: "kubernetes",
  cluster: "prod-eks-01",
  namespace: "default",
  region: "us-east-1",
  instrumentation: ["opentelemetry"],
  connectionMode: "agent",
  connectionEndpoint: "",
  discovery: "kubernetes-api",
  authMethod: "tokens",
  authSecretRef: "",
  authToken: "",
};

export function useWizardData() {
  const [data, setData] = useState<WizardData>(DEFAULT);
  const update = (patch: Partial<WizardData>) => setData((d) => ({ ...d, ...patch }));
  const reset = () => setData(DEFAULT);
  const toCreateInput = (): ServiceCreateInput => ({
    name: data.name,
    description: data.description,
    owner: data.owner,
    team: data.team,
    tags: data.tags,
    environment: data.environment,
    language: data.language,
    runtime: data.runtime,
    version: data.version,
    deployment: {
      type: data.deploymentType,
      cluster: data.cluster,
      namespace: data.namespace,
      region: data.region,
    },
    instrumentation: data.instrumentation,
    connection: {
      mode: data.connectionMode,
      endpoint: data.connectionEndpoint,
      discovery: data.discovery,
    },
    auth: {
      method: data.authMethod,
      secretRef: data.authSecretRef,
    },
  });
  return { data, update, reset, toCreateInput };
}
